import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useForm } from "@mantine/form";
import {
     Container,
     Paper,
     Title,
     Text,
     Stack,
     Group,
     Button,
     TextInput,
     NumberInput,
     Alert,
     Divider,
     Badge,
     ThemeIcon,
     PinInput,
     Center,
     Select,
     Loader,
} from "@mantine/core";
import SelfieCapture from "../../components/SelfieCapture";
import {
     IconPhone,
     IconCreditCard,
     IconCheck,
     IconAlertCircle,
     IconRefresh,
     IconUserCheck,
     IconLock,
     IconCamera,
     IconBuildingBank,
     IconX,
     IconMapPin,
     IconEdit,
} from "@tabler/icons-react";
import { useToastNotification } from "../../hooks/useToastNotification";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../types";
import { HttpCodes } from "../../lib/data";
import { useAuth } from "../../contexts/AuthContext";
import { isBvnComplete } from "../../lib/utils/checkBvnComplete";
import { IUserProps } from "../../types/User";

// Only allow banks that are supported by Paystack Direct Debit
// Source: https://paystack.com/docs/payments/direct-debit/#supported-banks
const SUPPORTED_DIRECT_DEBIT_BANKS = new Set<string>([
     "access bank",
     "citibank nigeria",
     "ecobank nigeria",
     "fidelity bank",
     "first bank of nigeria",
     "first city monument bank",
     "globus bank",
     "guaranty trust bank",
     "heritage bank",
     "jaiz bank",
     "keystone bank",
     "polaris bank",
     "premiumtrust bank",
     "providus bank",
     "stanbic ibtc bank",
     "standard chartered bank",
     "sterling bank",
     "suntrust bank",
     "titan bank",
     "union bank of nigeria",
     "united bank for africa",
     "unity bank",
     "wema bank",
     "zenith bank",
]);

export default function Profile() {
     const { user, reloadUser } = useAuth();
     const { success, error: showError } = useToastNotification();
     const [isInitializing, setIsInitializing] = useState(false);
     const [isCompleting, setIsCompleting] = useState(false);
     const [isVerifyingMandate, setIsVerifyingMandate] = useState(false);
     const [isRetryingActivation, setIsRetryingActivation] = useState(false);
     const [otpSent, setOtpSent] = useState(false);
     const [currentBvn, setCurrentBvn] = useState("");
     const [bvnComplete, setBvnComplete] = useState(false);
     const [selfieModalOpen, setSelfieModalOpen] = useState(false);
     const [isUploadingSelfie, setIsUploadingSelfie] = useState(false);
     const [cooldownTimer, setCooldownTimer] = useState(0);
     const [banks, setBanks] = useState<
          Array<{ value: string; label: string }>
     >([]);
     const [isLoadingBanks, setIsLoadingBanks] = useState(false);
     const [isSettingBankAccount, setIsSettingBankAccount] = useState(false);
     const [isEditingAddress, setIsEditingAddress] = useState(false);
     const [isUpdatingName, setIsUpdatingName] = useState(false);
     const [isUpdatingAddress, setIsUpdatingAddress] = useState(false);
     const bvnFormInitialized = useRef(false);

     const bvnForm = useForm({
          initialValues: {
               bvn: "",
               phone: "",
          },
          validate: {
               bvn: (value) => {
                    const digits = value.replace(/\D/g, "");
                    if (!value) return "BVN is required";
                    if (digits.length !== 11)
                         return "BVN must be exactly 11 digits";
                    return null;
               },
               phone: (value) => {
                    if (!value) return "Phone number is required";
                    const digits = value.replace(/\D/g, "");
                    if (digits.length !== 11)
                         return "Phone number must be exactly 11 digits";
                    if (!/^\d/.test(digits)) {
                         return "Phone number must start with a number";
                    }
                    return null;
               },
          },
     });

     const otpForm = useForm({
          initialValues: {
               otp: "",
          },
          validate: {
               otp: (value) => {
                    if (!value) return "OTP is required";
                    if (value.length !== 6)
                         return "OTP must be exactly 6 digits";
                    return null;
               },
          },
     });

     const bankAccountForm = useForm({
          initialValues: {
               accountNumber: "",
               bankCode: "",
          },
          validate: {
               accountNumber: (value) => {
                    const digits = value.replace(/\D/g, "");
                    if (!value) return "Account number is required";
                    if (digits.length !== 10)
                         return "Account number must be exactly 10 digits";
                    return null;
               },
               bankCode: (value) => {
                    if (!value) return "Bank is required";
                    return null;
               },
          },
     });

     const addressForm = useForm({
          initialValues: {
               address: "",
               nearestLandmark: "",
               busStop: "",
               city: "",
               localGovernmentArea: "",
               state: "",
               country: "",
          },
          validate: {
               address: (value) => {
                    if (value && value.length > 500)
                         return "Address must be less than 500 characters";
                    return null;
               },
               nearestLandmark: (value) => {
                    if (value && value.length > 200)
                         return "Nearest landmark must be less than 200 characters";
                    return null;
               },
               busStop: (value) => {
                    if (value && value.length > 200)
                         return "Bus stop must be less than 200 characters";
                    return null;
               },
               city: (value) => {
                    if (value && (value.length < 2 || value.length > 100))
                         return "City must be between 2 and 100 characters";
                    return null;
               },
               localGovernmentArea: (value) => {
                    if (value && (value.length < 2 || value.length > 100))
                         return "Local Government Area must be between 2 and 100 characters";
                    return null;
               },
               state: (value) => {
                    if (value && (value.length < 2 || value.length > 100))
                         return "State must be between 2 and 100 characters";
                    return null;
               },
               country: (value) => {
                    if (value && (value.length < 2 || value.length > 100))
                         return "Country must be between 2 and 100 characters";
                    return null;
               },
          },
     });

     const nameForm = useForm({
          initialValues: {
               firstName: "",
               lastName: "",
          },
          validate: {
               firstName: (value) => {
                    if (!value.trim()) return "First name is required";
                    if (value.trim().length < 2)
                         return "First name must be at least 2 characters";
                    return null;
               },
               lastName: (value) => {
                    if (!value.trim()) return "Last name is required";
                    if (value.trim().length < 2)
                         return "Last name must be at least 2 characters";
                    return null;
               },
          },
     });

     // Populate address form when user data is available
     useEffect(() => {
          if (user?.address && !isEditingAddress) {
               addressForm.setValues({
                    address: user.address.address || "",
                    nearestLandmark: user.address.nearestLandmark || "",
                    busStop: user.address.busStop || "",
                    city: user.address.city || "",
                    localGovernmentArea: user.address.localGovernmentArea || "",
                    state: user.address.state || "",
                    country: user.address.country || "",
               });
          }
     }, [user?.address, isEditingAddress]);

     // Check if BVN is already complete
     useEffect(() => {
          if (user) {
               const complete = isBvnComplete(user);
               setBvnComplete(complete);
               if (complete && user.bvnInfo && !bvnFormInitialized.current) {
                    // Pre-fill form with existing data (only once)
                    const newBvn =
                         user.bankVerificationNumber || user.bvnInfo.bvn || "";
                    const newPhone = user.bvnInfo.mobile || user.phone || "";

                    bvnForm.setValues({
                         bvn: newBvn,
                         phone: newPhone,
                    });
                    bvnFormInitialized.current = true;
               } else if (!complete) {
                    // Reset flag if BVN is not complete
                    bvnFormInitialized.current = false;
               }
          }
     }, [
          user?.bankVerificationNumber,
          user?.bvnInfo?.bvn,
          user?.bvnInfo?.mobile,
          user?.phone,
     ]);

     // Format seconds to MM:SS format
     const formatTimer = (seconds: number): string => {
          const mins = Math.floor(seconds / 60);
          const secs = seconds % 60;
          return `${mins}:${secs.toString().padStart(2, "0")}`;
     };

     // Cooldown timer effect
     useEffect(() => {
          if (cooldownTimer > 0) {
               const timer = setTimeout(() => {
                    setCooldownTimer(cooldownTimer - 1);
               }, 1000);
               return () => clearTimeout(timer);
          }
     }, [cooldownTimer]);

     // Fetch banks on component mount
     useEffect(() => {
          const fetchBanks = async () => {
               setIsLoadingBanks(true);
               try {
                    const response = await PerformRequest<
                         NonPaginatedResponse<any>
                    >({
                         method: Endpoints.GetBanks().method,
                         route: Endpoints.GetBanks().route,
                    });

                    if (
                         response &&
                         response.status === HttpCodes.Success &&
                         response.data?.data
                    ) {
                         // Use Set to filter duplicates by bank code
                         const seenCodes = new Set<string>();
                         const banksList = response.data.data
                              .filter((bank: any) => {
                                   // Only keep banks with code and name, and filter duplicates
                                   if (!bank.code || !bank.name) return false;

                                   // Ensure bank is supported for Direct Debit on Paystack
                                   const normalizedName = String(bank.name)
                                        .trim()
                                        .toLowerCase();
                                   if (
                                        !SUPPORTED_DIRECT_DEBIT_BANKS.has(
                                             normalizedName
                                        )
                                   ) {
                                        return false;
                                   }

                                   if (seenCodes.has(bank.code)) return false;
                                   seenCodes.add(bank.code);
                                   return true;
                              })
                              .map((bank: any) => ({
                                   value: bank.code,
                                   label: bank.name,
                              }));
                         setBanks(banksList);
                    }
               } catch (err) {
                    console.error("Error fetching banks:", err);
               } finally {
                    setIsLoadingBanks(false);
               }
          };

          fetchBanks();
     }, []);

     const handleInitializeBvn = async (values: {
          bvn: string;
          phone: string;
     }) => {
          setIsInitializing(true);
          try {
               const bvnDigits = values.bvn.replace(/\D/g, "");
               const phoneDigits = values.phone.replace(/\D/g, "");

               const r = await PerformRequest<NonPaginatedResponse<any>>({
                    method: Endpoints.GetInitializeBvnConsent().method,
                    route: Endpoints.GetInitializeBvnConsent().route,
                    data: {
                         bvn: bvnDigits,
                         phone: phoneDigits,
                    },
               });

               if (r && r.status === HttpCodes.Success) {
                    setCurrentBvn(bvnDigits);
                    setOtpSent(true);
                    setCooldownTimer(300); // Start 5-minute cooldown
                    success(
                         "OTP sent to your phone number. Please enter the OTP to complete BVN verification."
                    );
               } else {
                    showError(
                         r?.message ||
                              "Failed to initialize BVN verification. Please check your BVN and phone number."
                    );
               }
          } finally {
               setIsInitializing(false);
          }
     };

     const handleCompleteBvn = async (values: { otp: string }) => {
          if (!currentBvn) {
               showError(
                    "BVN not found. Please initialize BVN verification first."
               );
               return;
          }

          setIsCompleting(true);
          try {
               const response = await PerformRequest<NonPaginatedResponse<any>>(
                    {
                         method: Endpoints.GetCompleteBvnConsent().method,
                         route: Endpoints.GetCompleteBvnConsent().route,
                         data: {
                              bvn: currentBvn,
                              otp: values.otp,
                         },
                    }
               );

               if (
                    response &&
                    (response.status === HttpCodes.Success ||
                         response.status === 200)
               ) {
                    success(
                         "BVN verification completed successfully! Your profile has been updated."
                    );
                    setOtpSent(false);
                    setCurrentBvn("");
                    setCooldownTimer(300); // Set 5-minute cooldown after successful verification
                    otpForm.reset();
                    bvnForm.reset();
                    await reloadUser();
                    setBvnComplete(true);
               } else {
                    showError(
                         response?.message ||
                              "Failed to complete BVN verification. Please check your OTP."
                    );
               }
          } catch (err: any) {
               showError(
                    err?.message ||
                         "An error occurred while completing BVN verification"
               );
          } finally {
               setIsCompleting(false);
          }
     };

     const handleCaptureSelfie = async (file: File) => {
          if (!user?.bankVerificationNumber) {
               showError(
                    "BVN not found. Please complete BVN verification first."
               );
               return;
          }

          setIsUploadingSelfie(true);
          try {
               const formData = new FormData();
               formData.append("image", file);
               formData.append("bvn", user.bankVerificationNumber);

               const response = await PerformRequest<NonPaginatedResponse<any>>(
                    {
                         method: Endpoints.GetMatchBvnImage().method,
                         route: Endpoints.GetMatchBvnImage().route,
                         data: formData,
                         headers: {
                              "Content-Type": "multipart/form-data",
                         },
                    }
               );

               if (
                    response &&
                    (response.status === HttpCodes.Success ||
                         response.status === 200)
               ) {
                    const matchResult = response.data?.data || response.data;
                    if (matchResult?.match) {
                         success(
                              "Selfie verified successfully! Your BVN image verification is complete."
                         );
                         await reloadUser();
                         setSelfieModalOpen(false);
                    } else {
                         showError(
                              `Selfie verification failed. Similarity: ${
                                   matchResult?.similarity || 0
                              }%. Please try again with a clearer image.`
                         );
                    }
               } else {
                    showError(
                         response?.message ||
                              "Failed to verify selfie. Please try again."
                    );
               }
          } catch (err: any) {
               showError(
                    err?.message || "An error occurred while verifying selfie"
               );
          } finally {
               setIsUploadingSelfie(false);
          }
     };

     const handleSetBankAccount = async (values: {
          accountNumber: string;
          bankCode: string;
     }) => {
          setIsSettingBankAccount(true);
          try {
               const accountNumberDigits = values.accountNumber.replace(
                    /\D/g,
                    ""
               );

               const response = await PerformRequest<
                    NonPaginatedResponse<{
                         directDebitAuthorization: {
                              redirect_url: string;
                              reference: string;
                         };
                         user: IUserProps;
                    }>
               >({
                    method: Endpoints.GetSetBankAccount().method,
                    route: Endpoints.GetSetBankAccount().route,
                    data: {
                         accountNumber: accountNumberDigits,
                         bankCode: values.bankCode,
                    },
               });

               console.log("response", response);

               if (response && response.status === HttpCodes.Success) {
                    success("Bank account set successfully!");
                    bankAccountForm.reset();
                    await reloadUser();

                    // If backend initialized Paystack Direct Debit authorization, redirect user to grant consent
                    const directDebitAuth =
                         response.data?.data?.directDebitAuthorization;
                    const redirectUrl = directDebitAuth?.redirect_url;

                    if (redirectUrl) {
                         window.open(redirectUrl, "_blank");
                    }
               } else {
                    showError(
                         response?.message ||
                              "Failed to set bank account. Please check your account number and bank code."
                    );
               }
          } catch (err: any) {
               showError(
                    err?.message ||
                         "An error occurred while setting bank account"
               );
          } finally {
               setIsSettingBankAccount(false);
          }
     };

     const handleUpdateName = async (values: {
          firstName: string;
          lastName: string;
     }) => {
          setIsUpdatingName(true);
          try {
               const response = await PerformRequest<
                    NonPaginatedResponse<IUserProps>
               >({
                    method: Endpoints.GetUpdateName().method,
                    route: Endpoints.GetUpdateName().route,
                    data: {
                         firstName: values.firstName.trim(),
                         lastName: values.lastName.trim(),
                    },
               });

               if (response && response.status === HttpCodes.Success) {
                    success("Name updated successfully!");
                    await reloadUser();
               } else {
                    showError(
                         response?.message ||
                              "Failed to update name. Please check your input."
                    );
               }
          } catch (err: any) {
               showError(
                    err?.message || "An error occurred while updating your name"
               );
          } finally {
               setIsUpdatingName(false);
          }
     };

     const handleUpdateAddress = async (values: {
          address: string;
          nearestLandmark: string;
          busStop: string;
          city: string;
          localGovernmentArea: string;
          state: string;
          country: string;
     }) => {
          setIsUpdatingAddress(true);
          try {
               const response = await PerformRequest<NonPaginatedResponse<any>>(
                    {
                         method: Endpoints.GetUpdateBasicProfile().method,
                         route: Endpoints.GetUpdateBasicProfile().route,
                         data: {
                              address: values.address.trim() || undefined,
                              nearestLandmark:
                                   values.nearestLandmark.trim() || undefined,
                              busStop: values.busStop.trim() || undefined,
                              city: values.city.trim() || undefined,
                              localGovernmentArea:
                                   values.localGovernmentArea.trim() ||
                                   undefined,
                              state: values.state.trim() || undefined,
                              country: values.country.trim() || undefined,
                         },
                    }
               );

               if (response && response.status === HttpCodes.Success) {
                    success("Address updated successfully!");
                    setIsEditingAddress(false);
                    await reloadUser();
               } else {
                    showError(
                         response?.message ||
                              "Failed to update address. Please check your input."
                    );
               }
          } catch (err: any) {
               showError(
                    err?.message || "An error occurred while updating address"
               );
          } finally {
               setIsUpdatingAddress(false);
          }
     };

     // Check if user can set bank account
     const hasFullName = !!(user?.firstName && user?.lastName);
     const isBvnWaived = !!(user?.isBvnImageVerified && !user?.bvnInfo);
     const isBankAccountVerified = !!(
          user?.bankAccount && user?.isDirectDebitMandateActive
     );
     const isMandatePending = !!(
          user?.bankAccount && !user?.isDirectDebitMandateActive
     );
     const canSetBankAccount =
          user?.emailVerified &&
          user?.isBvnImageVerified &&
          user?.paystackCustomerCode &&
          hasFullName;

     if (bvnComplete) {
          return (
               <Container size="lg" py="xl">
                    <Stack gap="xl">
                         <motion.div
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5 }}
                         >
                              <Group gap="md" mb="md">
                                   <ThemeIcon
                                        size={56}
                                        radius="md"
                                        style={{
                                             background:
                                                  "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                        }}
                                   >
                                        <IconUserCheck
                                             size={28}
                                             style={{ color: "white" }}
                                        />
                                   </ThemeIcon>
                                   <div>
                                        <Title
                                             order={1}
                                             size="3rem"
                                             fw={800}
                                             style={{
                                                  color: "#679369",
                                                  marginBottom: "0.5rem",
                                             }}
                                        >
                                             Profile Complete
                                        </Title>
                                        <Text size="lg" c="dimmed">
                                             Your BVN verification has been
                                             completed successfully
                                        </Text>
                                   </div>
                              </Group>
                         </motion.div>

                         <Alert
                              icon={<IconCheck size={16} />}
                              title="BVN Verified"
                              color="green"
                              radius="md"
                         >
                              <Text size="sm">
                                   Your Bank Verification Number (BVN) has been
                                   successfully verified. Your profile
                                   information has been automatically updated
                                   from your BVN data. You can now proceed with
                                   KYC verification and loan requests.
                              </Text>
                         </Alert>

                         {user?.bvnInfo && (
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background: "white",
                                        border: "2px solid #e5e7eb",
                                   }}
                              >
                                   <Stack gap="md">
                                        <Title
                                             order={3}
                                             style={{ color: "#2C2C2C" }}
                                        >
                                             Your Profile Information
                                        </Title>
                                        <Divider />
                                        <Group grow>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       Full Name
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo.first_name}{" "}
                                                       {
                                                            user.bvnInfo
                                                                 .middle_name
                                                       }{" "}
                                                       {user.bvnInfo.last_name}
                                                  </Text>
                                             </div>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       Gender
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo.gender ||
                                                            "N/A"}
                                                  </Text>
                                             </div>
                                        </Group>
                                        <Group grow>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       Date of Birth
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo
                                                            .formatted_dob ||
                                                            user.bvnInfo.dob ||
                                                            "N/A"}
                                                  </Text>
                                             </div>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       Phone Number
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo.mobile ||
                                                            user.phone ||
                                                            "N/A"}
                                                  </Text>
                                             </div>
                                        </Group>
                                        <Group grow>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       State of Origin
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo
                                                            .state_of_origin ||
                                                            "N/A"}
                                                  </Text>
                                             </div>
                                             <div>
                                                  <Text
                                                       size="xs"
                                                       c="dimmed"
                                                       mb={4}
                                                  >
                                                       LGA of Origin
                                                  </Text>
                                                  <Text size="md" fw={500}>
                                                       {user.bvnInfo
                                                            .lga_of_origin ||
                                                            "N/A"}
                                                  </Text>
                                             </div>
                                        </Group>
                                        <div>
                                             <Text size="xs" c="dimmed" mb={4}>
                                                  Residential Address
                                             </Text>
                                             <Text size="md" fw={500}>
                                                  {user.bvnInfo
                                                       .residential_address ||
                                                       "N/A"}
                                             </Text>
                                        </div>
                                   </Stack>
                              </Paper>
                         )}

                         {/* BVN Image Verification Section */}
                         <Paper
                              shadow="md"
                              p="xl"
                              radius="lg"
                              style={{
                                   background: user?.isBvnImageVerified
                                        ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                                        : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                                   border: user?.isBvnImageVerified
                                        ? "2px solid #86efac"
                                        : "2px solid #fcd34d",
                              }}
                         >
                              <Stack gap="lg">
                                   <Group
                                        justify="space-between"
                                        align="flex-start"
                                   >
                                        <Group gap="md" align="flex-start">
                                             <ThemeIcon
                                                  size={56}
                                                  radius="md"
                                                  variant="light"
                                                  color={
                                                       user?.isBvnImageVerified
                                                            ? "green"
                                                            : "yellow"
                                                  }
                                             >
                                                  {user?.isBvnImageVerified ? (
                                                       <IconCheck size={28} />
                                                  ) : (
                                                       <IconCamera size={28} />
                                                  )}
                                             </ThemeIcon>
                                             <div>
                                                  <Title
                                                       order={3}
                                                       style={{
                                                            color: "#1f2937",
                                                       }}
                                                       mb={4}
                                                  >
                                                       BVN Image Verification
                                                  </Title>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       fw={500}
                                                  >
                                                       {user?.isBvnImageVerified
                                                            ? "Your selfie has been verified and matches your BVN photo"
                                                            : "Complete your verification by capturing a selfie that matches your BVN photo"}
                                                  </Text>
                                             </div>
                                        </Group>
                                        {user?.isBvnImageVerified ? (
                                             <Badge
                                                  color="green"
                                                  size="lg"
                                                  variant="filled"
                                                  radius="md"
                                                  style={{ fontWeight: 600 }}
                                             >
                                                  <Group gap={6}>
                                                       <IconCheck size={16} />
                                                       Verified
                                                  </Group>
                                             </Badge>
                                        ) : (
                                             <Badge
                                                  color="yellow"
                                                  size="lg"
                                                  variant="filled"
                                                  radius="md"
                                                  style={{
                                                       fontWeight: 600,
                                                       color: "#78350f",
                                                  }}
                                             >
                                                  PENDING
                                             </Badge>
                                        )}
                                   </Group>

                                   {user?.isBvnImageVerified ? (
                                        <Alert
                                             icon={<IconCheck size={20} />}
                                             color="green"
                                             radius="md"
                                             style={{
                                                  border: "1px solid #86efac",
                                             }}
                                        >
                                             <Text size="sm" fw={500}>
                                                  Verification Complete
                                             </Text>
                                             <Text size="sm" c="dimmed" mt={4}>
                                                  Your selfie has been
                                                  successfully verified and
                                                  matches your BVN photo. You
                                                  can now proceed with KYC
                                                  verification and loan
                                                  requests.
                                             </Text>
                                        </Alert>
                                   ) : (
                                        <Stack gap="md">
                                             <Alert
                                                  icon={
                                                       <IconAlertCircle
                                                            size={20}
                                                       />
                                                  }
                                                  color="yellow"
                                                  radius="md"
                                                  style={{
                                                       border: "1px solid #fcd34d",
                                                       background: "white",
                                                  }}
                                             >
                                                  <Group gap={8} mb="sm">
                                                       <IconCamera
                                                            size={16}
                                                            style={{
                                                                 color: "#78350f",
                                                            }}
                                                       />
                                                       <Text
                                                            size="sm"
                                                            fw={600}
                                                            style={{
                                                                 color: "#78350f",
                                                            }}
                                                       >
                                                            Selfie Requirements
                                                       </Text>
                                                  </Group>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       mb="xs"
                                                  >
                                                       Please ensure your selfie
                                                       meets these requirements:
                                                  </Text>
                                                  <Stack gap={4} mt="xs">
                                                       <Group gap={8}>
                                                            <IconCheck
                                                                 size={14}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                            <Text size="sm">
                                                                 Your face is
                                                                 clearly visible
                                                                 and well-lit
                                                            </Text>
                                                       </Group>
                                                       <Group gap={8}>
                                                            <IconCheck
                                                                 size={14}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                            <Text size="sm">
                                                                 No caps, hats,
                                                                 or head
                                                                 coverings
                                                            </Text>
                                                       </Group>
                                                       <Group gap={8}>
                                                            <IconCheck
                                                                 size={14}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                            <Text size="sm">
                                                                 No glasses
                                                            </Text>
                                                       </Group>
                                                       <Group gap={8}>
                                                            <IconCheck
                                                                 size={14}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                            <Text size="sm">
                                                                 Good lighting
                                                                 conditions
                                                            </Text>
                                                       </Group>
                                                  </Stack>
                                             </Alert>

                                             <Alert
                                                  color="red"
                                                  radius="md"
                                                  style={{
                                                       border: "1px solid #fca5a5",
                                                       background: "#fef2f2",
                                                  }}
                                             >
                                                  <Group gap={8} mb={4}>
                                                       <IconAlertCircle
                                                            size={16}
                                                            style={{
                                                                 color: "#991b1b",
                                                            }}
                                                       />
                                                       <Text
                                                            size="sm"
                                                            fw={600}
                                                            style={{
                                                                 color: "#991b1b",
                                                            }}
                                                       >
                                                            Access Restriction
                                                       </Text>
                                                  </Group>
                                                  <Text
                                                       size="sm"
                                                       style={{
                                                            color: "#7f1d1d",
                                                       }}
                                                  >
                                                       You must complete BVN
                                                       image verification before
                                                       you can access KYC
                                                       verification or request
                                                       loans.
                                                  </Text>
                                             </Alert>

                                             <Button
                                                  leftSection={
                                                       <IconCamera size={20} />
                                                  }
                                                  onClick={() =>
                                                       setSelfieModalOpen(true)
                                                  }
                                                  size="lg"
                                                  radius="md"
                                                  style={{
                                                       background:
                                                            "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                                       fontWeight: 600,
                                                  }}
                                                  fullWidth
                                             >
                                                  Capture Selfie Now
                                             </Button>
                                        </Stack>
                                   )}
                              </Stack>
                         </Paper>

                         {/* Bank Account Section */}
                         {canSetBankAccount && (
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background: user?.bankAccount
                                             ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                                             : "white",
                                        border: user?.bankAccount
                                             ? "2px solid #86efac"
                                             : "2px solid #e5e7eb",
                                   }}
                              >
                                   <Stack gap="lg">
                                        <Group
                                             justify="space-between"
                                             align="flex-start"
                                        >
                                             <Group gap="md" align="flex-start">
                                                  <ThemeIcon
                                                       size={56}
                                                       radius="md"
                                                       variant="light"
                                                       color={
                                                            user?.bankAccount
                                                                 ? "green"
                                                                 : "blue"
                                                       }
                                                  >
                                                       <IconBuildingBank
                                                            size={28}
                                                       />
                                                  </ThemeIcon>
                                                  <div>
                                                       <Title
                                                            order={3}
                                                            style={{
                                                                 color: "#1f2937",
                                                            }}
                                                            mb={4}
                                                       >
                                                            Bank Account
                                                       </Title>
                                                       <Text
                                                            size="sm"
                                                            c="dimmed"
                                                            fw={500}
                                                       >
                                                            {user?.bankAccount
                                                                 ? "Your bank account has been set up successfully"
                                                                 : "Set up your bank account for loan disbursements"}
                                                       </Text>
                                                  </div>
                                             </Group>
                                             {user?.bankAccount && (
                                                  <Badge
                                                       color={
                                                            isBankAccountVerified
                                                                 ? "green"
                                                                 : "yellow"
                                                       }
                                                       size="lg"
                                                       variant="filled"
                                                       radius="md"
                                                       style={{
                                                            fontWeight: 600,
                                                       }}
                                                  >
                                                       <Group gap={6}>
                                                            <IconCheck
                                                                 size={16}
                                                            />
                                                            {isBankAccountVerified
                                                                 ? "Verified"
                                                                 : "Pending Mandate"}
                                                       </Group>
                                                  </Badge>
                                             )}
                                        </Group>

                                        {user?.bankAccount ? (
                                             <Alert
                                                  icon={<IconCheck size={20} />}
                                                  color="green"
                                                  radius="md"
                                                  style={{
                                                       border: "1px solid #86efac",
                                                  }}
                                             >
                                                  <Text size="sm" fw={500}>
                                                       {isBankAccountVerified
                                                            ? "Bank Account Verified"
                                                            : "Bank Account Set • Awaiting Direct Debit Authorization"}
                                                  </Text>
                                                  <Stack gap="xs" mt="md">
                                                       <Group gap="md">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Account Name:
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      user
                                                                           .bankAccount
                                                                           .accountName
                                                                 }
                                                            </Text>
                                                       </Group>
                                                       <Group gap="md">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Account Number:
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {
                                                                      user
                                                                           .bankAccount
                                                                           .accountNumber
                                                                 }
                                                            </Text>
                                                       </Group>
                                                       <Group gap="md">
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 Bank:
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 fw={600}
                                                            >
                                                                 {user
                                                                      .bankAccount
                                                                      .bankName ||
                                                                      "N/A"}
                                                            </Text>
                                                       </Group>
                                                  </Stack>

                                                  {isMandatePending && (
                                                       <Stack gap="xs" mt="md">
                                                            <Alert
                                                                 icon={
                                                                      <IconAlertCircle
                                                                           size={
                                                                                18
                                                                           }
                                                                      />
                                                                 }
                                                                 color="yellow"
                                                                 radius="md"
                                                                 style={{
                                                                      border: "1px solid #facc15",
                                                                 }}
                                                            >
                                                                 <Text
                                                                      size="sm"
                                                                      fw={500}
                                                                 >
                                                                      Direct
                                                                      debit
                                                                      mandate
                                                                      pending
                                                                 </Text>
                                                                 <Text
                                                                      size="xs"
                                                                      c="dimmed"
                                                                 >
                                                                      If
                                                                      Paystack
                                                                      did not
                                                                      prompt you
                                                                      to
                                                                      complete
                                                                      the
                                                                      mandate,
                                                                      you can
                                                                      retry the
                                                                      setup.
                                                                 </Text>
                                                                 {user
                                                                      ?.directDebitMandate
                                                                      ?.reference && (
                                                                      <Text
                                                                           size="xs"
                                                                           c="dimmed"
                                                                           mt={
                                                                                6
                                                                           }
                                                                      >
                                                                           Reference:{" "}
                                                                           {
                                                                                user
                                                                                     .directDebitMandate
                                                                                     .reference
                                                                           }
                                                                      </Text>
                                                                 )}
                                                                 {user
                                                                      ?.directDebitMandate
                                                                      ?.lastError && (
                                                                      <Text
                                                                           size="xs"
                                                                           c="dimmed"
                                                                           mt={
                                                                                4
                                                                           }
                                                                      >
                                                                           Last
                                                                           error:{" "}
                                                                           {
                                                                                user
                                                                                     .directDebitMandate
                                                                                     .lastError
                                                                           }
                                                                      </Text>
                                                                 )}
                                                            </Alert>
                                                            <Group justify="flex-start">
                                                                 <Button
                                                                      size="xs"
                                                                      variant="light"
                                                                      color="blue"
                                                                      loading={
                                                                           isVerifyingMandate
                                                                      }
                                                                      onClick={async () => {
                                                                           try {
                                                                                setIsVerifyingMandate(
                                                                                     true
                                                                                );
                                                                                const response =
                                                                                     await PerformRequest<
                                                                                          NonPaginatedResponse<{
                                                                                               user: IUserProps;
                                                                                          }>
                                                                                     >(
                                                                                          {
                                                                                               method: Endpoints.GetVerifyDirectDebitMandate()
                                                                                                    .method,
                                                                                               route: Endpoints.GetVerifyDirectDebitMandate()
                                                                                                    .route,
                                                                                               data: {
                                                                                                    reference:
                                                                                                         user
                                                                                                              ?.directDebitMandate
                                                                                                              ?.reference,
                                                                                               },
                                                                                          }
                                                                                     );

                                                                                if (
                                                                                     response &&
                                                                                     response.status ===
                                                                                          HttpCodes.Success
                                                                                ) {
                                                                                     success(
                                                                                          "Mandate status refreshed"
                                                                                     );
                                                                                     await reloadUser();
                                                                                } else {
                                                                                     showError(
                                                                                          response?.message ||
                                                                                               "Failed to verify mandate status. Please try again."
                                                                                     );
                                                                                }
                                                                           } catch (err: any) {
                                                                                showError(
                                                                                     err?.message ||
                                                                                          "An error occurred while verifying mandate status"
                                                                                );
                                                                           } finally {
                                                                                setIsVerifyingMandate(
                                                                                     false
                                                                                );
                                                                           }
                                                                      }}
                                                                 >
                                                                      Verify
                                                                      Status
                                                                 </Button>

                                                                 <Button
                                                                      size="xs"
                                                                      variant="light"
                                                                      color="green"
                                                                      loading={
                                                                           isRetryingActivation
                                                                      }
                                                                      onClick={async () => {
                                                                           try {
                                                                                setIsRetryingActivation(
                                                                                     true
                                                                                );
                                                                                const response =
                                                                                     await PerformRequest<
                                                                                          NonPaginatedResponse<any>
                                                                                     >(
                                                                                          {
                                                                                               method: Endpoints.GetRetryDirectDebitActivation()
                                                                                                    .method,
                                                                                               route: Endpoints.GetRetryDirectDebitActivation()
                                                                                                    .route,
                                                                                          }
                                                                                     );

                                                                                if (
                                                                                     response &&
                                                                                     response.status ===
                                                                                          HttpCodes.Success
                                                                                ) {
                                                                                     success(
                                                                                          "Activation retry requested. Please wait a moment and verify status."
                                                                                     );
                                                                                     await reloadUser();
                                                                                } else {
                                                                                     showError(
                                                                                          response?.message ||
                                                                                               "Failed to retry activation. Please try again."
                                                                                     );
                                                                                }
                                                                           } catch (err: any) {
                                                                                showError(
                                                                                     err?.message ||
                                                                                          "An error occurred while retrying activation"
                                                                                );
                                                                           } finally {
                                                                                setIsRetryingActivation(
                                                                                     false
                                                                                );
                                                                           }
                                                                      }}
                                                                 >
                                                                      Retry
                                                                      Activation
                                                                 </Button>

                                                                 <Button
                                                                      size="xs"
                                                                      variant="light"
                                                                      color="yellow"
                                                                      onClick={async () => {
                                                                           try {
                                                                                const response =
                                                                                     await PerformRequest<
                                                                                          NonPaginatedResponse<{
                                                                                               directDebitAuthorization?: {
                                                                                                    redirect_url: string;
                                                                                                    reference: string;
                                                                                               };
                                                                                               user: IUserProps;
                                                                                          }>
                                                                                     >(
                                                                                          {
                                                                                               method: Endpoints.GetRetryDirectDebitMandate()
                                                                                                    .method,
                                                                                               route: Endpoints.GetRetryDirectDebitMandate()
                                                                                                    .route,
                                                                                          }
                                                                                     );

                                                                                if (
                                                                                     response &&
                                                                                     response.status ===
                                                                                          HttpCodes.Success
                                                                                ) {
                                                                                     success(
                                                                                          "Direct debit mandate retry initialized. Redirecting you to Paystack..."
                                                                                     );
                                                                                     const directDebitAuth =
                                                                                          response
                                                                                               .data
                                                                                               ?.data
                                                                                               ?.directDebitAuthorization;
                                                                                     const redirectUrl =
                                                                                          directDebitAuth?.redirect_url;
                                                                                     await reloadUser();
                                                                                     if (
                                                                                          redirectUrl
                                                                                     ) {
                                                                                          window.open(
                                                                                               redirectUrl,
                                                                                               "_blank"
                                                                                          );
                                                                                     }
                                                                                } else {
                                                                                     showError(
                                                                                          response?.message ||
                                                                                               "Failed to retry direct debit mandate. Please try again."
                                                                                     );
                                                                                }
                                                                           } catch (err: any) {
                                                                                showError(
                                                                                     err?.message ||
                                                                                          "An error occurred while retrying direct debit mandate"
                                                                                );
                                                                           }
                                                                      }}
                                                                 >
                                                                      Retry
                                                                      Mandate
                                                                      Setup
                                                                 </Button>
                                                            </Group>
                                                       </Stack>
                                                  )}
                                             </Alert>
                                        ) : (
                                             <Stack gap="md">
                                                  <Alert
                                                       icon={
                                                            <IconAlertCircle
                                                                 size={20}
                                                            />
                                                       }
                                                       color="blue"
                                                       radius="md"
                                                       style={{
                                                            border: "1px solid #93c5fd",
                                                            background:
                                                                 "#eff6ff",
                                                       }}
                                                  >
                                                       <Text
                                                            size="sm"
                                                            fw={600}
                                                            style={{
                                                                 color: "#1e40af",
                                                            }}
                                                            mb={4}
                                                       >
                                                            Account Name
                                                            Verification
                                                       </Text>
                                                       <Text
                                                            size="sm"
                                                            style={{
                                                                 color: "#1e3a8a",
                                                            }}
                                                       >
                                                            The account name
                                                            must match your
                                                            registered name (
                                                            {user?.firstName}{" "}
                                                            {user?.lastName}).
                                                       </Text>
                                                  </Alert>

                                                  <form
                                                       onSubmit={bankAccountForm.onSubmit(
                                                            handleSetBankAccount
                                                       )}
                                                  >
                                                       <Stack gap="md">
                                                            <Select
                                                                 label="Select Bank"
                                                                 placeholder="Choose your bank"
                                                                 data={banks}
                                                                 leftSection={
                                                                      <IconBuildingBank
                                                                           size={
                                                                                18
                                                                           }
                                                                      />
                                                                 }
                                                                 searchable
                                                                 required
                                                                 disabled={
                                                                      isLoadingBanks ||
                                                                      isSettingBankAccount
                                                                 }
                                                                 {...bankAccountForm.getInputProps(
                                                                      "bankCode"
                                                                 )}
                                                                 rightSection={
                                                                      isLoadingBanks ? (
                                                                           <Loader size="sm" />
                                                                      ) : null
                                                                 }
                                                            />

                                                            <TextInput
                                                                 label="Account Number"
                                                                 placeholder="Enter your 10-digit account number"
                                                                 leftSection={
                                                                      <IconCreditCard
                                                                           size={
                                                                                18
                                                                           }
                                                                      />
                                                                 }
                                                                 maxLength={10}
                                                                 required
                                                                 disabled={
                                                                      isSettingBankAccount
                                                                 }
                                                                 {...bankAccountForm.getInputProps(
                                                                      "accountNumber"
                                                                 )}
                                                                 onChange={(
                                                                      event
                                                                 ) => {
                                                                      const value =
                                                                           event
                                                                                .currentTarget
                                                                                .value;
                                                                      // Only allow digits, preserve leading zeros
                                                                      const numeric =
                                                                           value
                                                                                .replace(
                                                                                     /\D/g,
                                                                                     ""
                                                                                )
                                                                                .slice(
                                                                                     0,
                                                                                     10
                                                                                );
                                                                      bankAccountForm.setFieldValue(
                                                                           "accountNumber",
                                                                           numeric
                                                                      );
                                                                 }}
                                                            />

                                                            <Button
                                                                 type="submit"
                                                                 loading={
                                                                      isSettingBankAccount
                                                                 }
                                                                 leftSection={
                                                                      <IconCheck
                                                                           size={
                                                                                20
                                                                           }
                                                                      />
                                                                 }
                                                                 size="lg"
                                                                 radius="md"
                                                                 style={{
                                                                      background:
                                                                           "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                                                      fontWeight: 600,
                                                                 }}
                                                                 fullWidth
                                                            >
                                                                 {isSettingBankAccount
                                                                      ? "Setting Bank Account..."
                                                                      : "Set Bank Account"}
                                                            </Button>
                                                       </Stack>
                                                  </form>
                                             </Stack>
                                        )}
                                   </Stack>
                              </Paper>
                         )}

                         {!canSetBankAccount && (
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background:
                                             "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                                        border: "2px solid #fcd34d",
                                   }}
                              >
                                   <Stack gap="md">
                                        <Group gap="md" align="flex-start">
                                             <ThemeIcon
                                                  size={56}
                                                  radius="md"
                                                  variant="light"
                                                  color="yellow"
                                             >
                                                  <IconBuildingBank size={28} />
                                             </ThemeIcon>
                                             <div style={{ flex: 1 }}>
                                                  <Title
                                                       order={3}
                                                       style={{
                                                            color: "#1f2937",
                                                       }}
                                                       mb={4}
                                                  >
                                                       Bank Account Setup
                                                  </Title>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       fw={500}
                                                  >
                                                       Complete the following
                                                       requirements to set up
                                                       your bank account
                                                  </Text>
                                             </div>
                                        </Group>

                                        <Alert
                                             icon={
                                                  <IconAlertCircle size={20} />
                                             }
                                             color="yellow"
                                             radius="md"
                                             style={{
                                                  border: "1px solid #fcd34d",
                                                  background: "white",
                                             }}
                                        >
                                             <Text
                                                  size="sm"
                                                  fw={600}
                                                  style={{ color: "#78350f" }}
                                                  mb="sm"
                                             >
                                                  Requirements Not Met
                                             </Text>
                                             <Stack gap={4}>
                                                  <Group gap={8}>
                                                       {user?.emailVerified ? (
                                                            <IconCheck
                                                                 size={16}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                       ) : (
                                                            <IconX
                                                                 size={16}
                                                                 style={{
                                                                      color: "#dc2626",
                                                                 }}
                                                            />
                                                       )}
                                                       <Text size="sm">
                                                            Email Verification:{" "}
                                                            {user?.emailVerified
                                                                 ? "Complete"
                                                                 : "Pending"}
                                                       </Text>
                                                  </Group>
                                                  <Group gap={8}>
                                                       {user?.isBvnImageVerified ? (
                                                            <IconCheck
                                                                 size={16}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                       ) : (
                                                            <IconX
                                                                 size={16}
                                                                 style={{
                                                                      color: "#dc2626",
                                                                 }}
                                                            />
                                                       )}
                                                       <Text size="sm">
                                                            BVN Image
                                                            Verification:{" "}
                                                            {user?.isBvnImageVerified
                                                                 ? "Complete"
                                                                 : "Pending"}
                                                       </Text>
                                                  </Group>
                                                  <Group gap={8}>
                                                       {user?.paystackCustomerCode ? (
                                                            <IconCheck
                                                                 size={16}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                       ) : (
                                                            <IconX
                                                                 size={16}
                                                                 style={{
                                                                      color: "#dc2626",
                                                                 }}
                                                            />
                                                       )}
                                                       <Text size="sm">
                                                            Paystack Customer
                                                            Code:{" "}
                                                            {user?.paystackCustomerCode
                                                                 ? "Set"
                                                                 : "Not Set"}
                                                       </Text>
                                                  </Group>
                                                  <Group gap={8}>
                                                       {hasFullName ? (
                                                            <IconCheck
                                                                 size={16}
                                                                 style={{
                                                                      color: "#679369",
                                                                 }}
                                                            />
                                                       ) : (
                                                            <IconX
                                                                 size={16}
                                                                 style={{
                                                                      color: "#dc2626",
                                                                 }}
                                                            />
                                                       )}
                                                       <Text size="sm">
                                                            Name on Profile
                                                            (First &amp; Last):{" "}
                                                            {hasFullName
                                                                 ? "Complete"
                                                                 : "Pending"}
                                                       </Text>
                                                  </Group>
                                             </Stack>
                                        </Alert>
                                   </Stack>
                              </Paper>
                         )}

                         {/* Name Setup for BVN-Waived Users */}
                         {isBvnWaived && !hasFullName && (
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background: "white",
                                        border: "2px solid #e5e7eb",
                                   }}
                              >
                                   <Stack gap="lg">
                                        <Group
                                             justify="space-between"
                                             align="flex-start"
                                        >
                                             <Group gap="md" align="flex-start">
                                                  <ThemeIcon
                                                       size={56}
                                                       radius="md"
                                                       variant="light"
                                                       color="blue"
                                                  >
                                                       <IconUserCheck
                                                            size={28}
                                                       />
                                                  </ThemeIcon>
                                                  <div>
                                                       <Title
                                                            order={3}
                                                            style={{
                                                                 color: "#1f2937",
                                                            }}
                                                            mb={4}
                                                       >
                                                            Set Your Name
                                                       </Title>
                                                       <Text
                                                            size="sm"
                                                            c="dimmed"
                                                            fw={500}
                                                       >
                                                            Enter your first and
                                                            last name exactly as
                                                            it appears on your
                                                            bank account.
                                                       </Text>
                                                  </div>
                                             </Group>
                                        </Group>

                                        <form
                                             onSubmit={nameForm.onSubmit(
                                                  handleUpdateName
                                             )}
                                        >
                                             <Stack gap="md">
                                                  <Group grow>
                                                       <TextInput
                                                            label="First Name"
                                                            placeholder="Enter your first name"
                                                            required
                                                            {...nameForm.getInputProps(
                                                                 "firstName"
                                                            )}
                                                       />
                                                       <TextInput
                                                            label="Last Name"
                                                            placeholder="Enter your last name"
                                                            required
                                                            {...nameForm.getInputProps(
                                                                 "lastName"
                                                            )}
                                                       />
                                                  </Group>

                                                  <Button
                                                       type="submit"
                                                       loading={isUpdatingName}
                                                       leftSection={
                                                            <IconCheck
                                                                 size={20}
                                                            />
                                                       }
                                                       size="lg"
                                                       radius="md"
                                                       style={{
                                                            background:
                                                                 "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                                            fontWeight: 600,
                                                       }}
                                                  >
                                                       Save Name
                                                  </Button>
                                             </Stack>
                                        </form>
                                   </Stack>
                              </Paper>
                         )}

                         {/* Address Section */}
                         <Paper
                              shadow="md"
                              p="xl"
                              radius="lg"
                              style={{
                                   background: user?.address
                                        ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                                        : "white",
                                   border: user?.address
                                        ? "2px solid #86efac"
                                        : "2px solid #e5e7eb",
                              }}
                         >
                              <Stack gap="lg">
                                   <Group
                                        justify="space-between"
                                        align="flex-start"
                                   >
                                        <Group gap="md" align="flex-start">
                                             <ThemeIcon
                                                  size={56}
                                                  radius="md"
                                                  variant="light"
                                                  color={
                                                       user?.address
                                                            ? "green"
                                                            : "blue"
                                                  }
                                             >
                                                  <IconMapPin size={28} />
                                             </ThemeIcon>
                                             <div>
                                                  <Title
                                                       order={3}
                                                       style={{
                                                            color: "#1f2937",
                                                       }}
                                                       mb={4}
                                                  >
                                                       Address Information
                                                  </Title>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       fw={500}
                                                  >
                                                       {user?.address
                                                            ? "Update your residential address"
                                                            : "Add your residential address"}
                                                  </Text>
                                             </div>
                                        </Group>
                                        {user?.address && !isEditingAddress && (
                                             <Button
                                                  variant="subtle"
                                                  leftSection={
                                                       <IconEdit size={18} />
                                                  }
                                                  onClick={() =>
                                                       setIsEditingAddress(true)
                                                  }
                                                  style={{ color: "#679369" }}
                                             >
                                                  Edit
                                             </Button>
                                        )}
                                   </Group>

                                   {isEditingAddress ? (
                                        <form
                                             onSubmit={addressForm.onSubmit(
                                                  handleUpdateAddress
                                             )}
                                        >
                                             <Stack gap="md">
                                                  <TextInput
                                                       label="Address"
                                                       placeholder="Enter your full address"
                                                       leftSection={
                                                            <IconMapPin
                                                                 size={18}
                                                            />
                                                       }
                                                       maxLength={500}
                                                       {...addressForm.getInputProps(
                                                            "address"
                                                       )}
                                                  />
                                                  <TextInput
                                                       label="Nearest Landmark"
                                                       placeholder="e.g. Central Park"
                                                       leftSection={
                                                            <IconMapPin
                                                                 size={18}
                                                            />
                                                       }
                                                       maxLength={200}
                                                       {...addressForm.getInputProps(
                                                            "nearestLandmark"
                                                       )}
                                                  />

                                                  <TextInput
                                                       label="Bus Stop"
                                                       placeholder="e.g. Central Park Bus Stop"
                                                       leftSection={
                                                            <IconMapPin
                                                                 size={18}
                                                            />
                                                       }
                                                       maxLength={200}
                                                       {...addressForm.getInputProps(
                                                            "busStop"
                                                       )}
                                                  />

                                                  <Group grow>
                                                       <TextInput
                                                            label="City"
                                                            placeholder="Enter your city"
                                                            leftSection={
                                                                 <IconMapPin
                                                                      size={18}
                                                                 />
                                                            }
                                                            maxLength={100}
                                                            {...addressForm.getInputProps(
                                                                 "city"
                                                            )}
                                                       />
                                                       <TextInput
                                                            label="State"
                                                            placeholder="Enter your state"
                                                            leftSection={
                                                                 <IconMapPin
                                                                      size={18}
                                                                 />
                                                            }
                                                            maxLength={100}
                                                            {...addressForm.getInputProps(
                                                                 "state"
                                                            )}
                                                       />
                                                  </Group>

                                                  <Group grow>
                                                       <TextInput
                                                            label="Local Government Area"
                                                            placeholder="Enter your LGA"
                                                            leftSection={
                                                                 <IconMapPin
                                                                      size={18}
                                                                 />
                                                            }
                                                            maxLength={100}
                                                            {...addressForm.getInputProps(
                                                                 "localGovernmentArea"
                                                            )}
                                                       />
                                                       <TextInput
                                                            label="Country"
                                                            placeholder="Enter your country"
                                                            leftSection={
                                                                 <IconMapPin
                                                                      size={18}
                                                                 />
                                                            }
                                                            maxLength={100}
                                                            {...addressForm.getInputProps(
                                                                 "country"
                                                            )}
                                                       />
                                                  </Group>

                                                  <Group
                                                       justify="flex-end"
                                                       mt="md"
                                                  >
                                                       <Button
                                                            variant="subtle"
                                                            onClick={() => {
                                                                 setIsEditingAddress(
                                                                      false
                                                                 );
                                                                 addressForm.setValues(
                                                                      {
                                                                           nearestLandmark:
                                                                                user
                                                                                     ?.address
                                                                                     ?.nearestLandmark ||
                                                                                "",
                                                                           busStop:
                                                                                user
                                                                                     ?.address
                                                                                     ?.busStop ||
                                                                                "",
                                                                           city:
                                                                                user
                                                                                     ?.address
                                                                                     ?.city ||
                                                                                "",
                                                                           localGovernmentArea:
                                                                                user
                                                                                     ?.address
                                                                                     ?.localGovernmentArea ||
                                                                                "",
                                                                           state:
                                                                                user
                                                                                     ?.address
                                                                                     ?.state ||
                                                                                "",
                                                                           country:
                                                                                user
                                                                                     ?.address
                                                                                     ?.country ||
                                                                                "",
                                                                      }
                                                                 );
                                                            }}
                                                            disabled={
                                                                 isUpdatingAddress
                                                            }
                                                       >
                                                            Cancel
                                                       </Button>
                                                       <Button
                                                            type="submit"
                                                            loading={
                                                                 isUpdatingAddress
                                                            }
                                                            leftSection={
                                                                 <IconCheck
                                                                      size={18}
                                                                 />
                                                            }
                                                            style={{
                                                                 background:
                                                                      "#679369",
                                                            }}
                                                       >
                                                            {isUpdatingAddress
                                                                 ? "Updating..."
                                                                 : "Update Address"}
                                                       </Button>
                                                  </Group>
                                             </Stack>
                                        </form>
                                   ) : user?.address ? (
                                        <Stack gap="md">
                                             <Alert
                                                  icon={<IconCheck size={20} />}
                                                  color="green"
                                                  radius="md"
                                                  style={{
                                                       border: "1px solid #86efac",
                                                  }}
                                             >
                                                  <Text size="sm" fw={500}>
                                                       Address Information
                                                  </Text>
                                                  <Stack gap="xs" mt="md">
                                                       {user.address
                                                            .nearestLandmark && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      Nearest
                                                                      Landmark:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .nearestLandmark
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                       {user.address
                                                            .busStop && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      Bus Stop:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .busStop
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                       {user.address.city && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      City:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .city
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                       {user.address.state && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      State:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .state
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                       {user.address
                                                            .localGovernmentArea && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      Local
                                                                      Government
                                                                      Area:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .localGovernmentArea
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                       {user.address
                                                            .country && (
                                                            <Group gap="md">
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                 >
                                                                      Country:
                                                                 </Text>
                                                                 <Text
                                                                      size="sm"
                                                                      fw={600}
                                                                 >
                                                                      {
                                                                           user
                                                                                .address
                                                                                .country
                                                                      }
                                                                 </Text>
                                                            </Group>
                                                       )}
                                                  </Stack>
                                             </Alert>
                                        </Stack>
                                   ) : (
                                        <Stack gap="md">
                                             <Alert
                                                  icon={
                                                       <IconAlertCircle
                                                            size={20}
                                                       />
                                                  }
                                                  color="blue"
                                                  radius="md"
                                             >
                                                  <Text size="sm" fw={500}>
                                                       No Address Information
                                                  </Text>
                                                  <Text size="sm" mt="xs">
                                                       Add your residential
                                                       address to complete your
                                                       profile.
                                                  </Text>
                                             </Alert>
                                             <Button
                                                  leftSection={
                                                       <IconEdit size={18} />
                                                  }
                                                  onClick={() =>
                                                       setIsEditingAddress(true)
                                                  }
                                                  size="lg"
                                                  style={{
                                                       background: "#679369",
                                                  }}
                                             >
                                                  Add Address
                                             </Button>
                                        </Stack>
                                   )}
                              </Stack>
                         </Paper>
                    </Stack>

                    <SelfieCapture
                         opened={selfieModalOpen}
                         onClose={() => setSelfieModalOpen(false)}
                         onCapture={handleCaptureSelfie}
                         bvn={user?.bankVerificationNumber || ""}
                         isUploading={isUploadingSelfie}
                    />
               </Container>
          );
     }

     return (
          <Container size="lg" py="xl">
               <Stack gap="xl">
                    {/* Header Section */}
                    <motion.div
                         initial={{ opacity: 0, y: -20 }}
                         animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.5 }}
                    >
                         <Group gap="md" mb="md">
                              <ThemeIcon
                                   size={56}
                                   radius="md"
                                   style={{
                                        background:
                                             "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                   }}
                              >
                                   <IconUserCheck
                                        size={28}
                                        style={{ color: "white" }}
                                   />
                              </ThemeIcon>
                              <div>
                                   <Title
                                        order={1}
                                        size="3rem"
                                        fw={800}
                                        style={{
                                             color: "#679369",
                                             marginBottom: "0.5rem",
                                        }}
                                   >
                                        Complete Your Profile
                                   </Title>
                                   <Text size="lg" c="dimmed">
                                        Verify your BVN to automatically
                                        complete your profile information
                                   </Text>
                              </div>
                         </Group>
                    </motion.div>

                    {/* BVN and Phone Input Section */}
                    {!otpSent && (
                         <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4 }}
                         >
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background: "white",
                                        border: "2px solid #e5e7eb",
                                   }}
                              >
                                   <Stack gap="lg">
                                        <div>
                                             <Title
                                                  order={3}
                                                  mb="xs"
                                                  style={{ color: "#2C2C2C" }}
                                             >
                                                  Enter Your BVN Details
                                             </Title>
                                             <Text size="sm" c="dimmed">
                                                  An OTP will be sent to your
                                                  phone number to complete the
                                                  verification.
                                             </Text>
                                        </div>

                                        <form
                                             onSubmit={bvnForm.onSubmit(
                                                  handleInitializeBvn
                                             )}
                                        >
                                             <Stack gap="md">
                                                  <NumberInput
                                                       label="Bank Verification Number (BVN)"
                                                       placeholder="Enter your 11-digit BVN"
                                                       leftSection={
                                                            <IconCreditCard
                                                                 size={18}
                                                            />
                                                       }
                                                       hideControls
                                                       maxLength={11}
                                                       min={0}
                                                       required
                                                       size="md"
                                                       {...bvnForm.getInputProps(
                                                            "bvn"
                                                       )}
                                                       onChange={(val) => {
                                                            const str =
                                                                 typeof val ===
                                                                 "number"
                                                                      ? String(
                                                                             val
                                                                        )
                                                                      : val ||
                                                                        "";
                                                            const numeric = str
                                                                 .replace(
                                                                      /\D/g,
                                                                      ""
                                                                 )
                                                                 .slice(0, 11);
                                                            bvnForm.setFieldValue(
                                                                 "bvn",
                                                                 numeric
                                                            );
                                                       }}
                                                       styles={{
                                                            input: {
                                                                 fontSize:
                                                                      "16px",
                                                                 fontWeight: 500,
                                                            },
                                                       }}
                                                  />

                                                  <TextInput
                                                       label="Nigerian Phone Number"
                                                       placeholder="01234567890"
                                                       leftSection={
                                                            <IconPhone
                                                                 size={18}
                                                            />
                                                       }
                                                       required
                                                       size="md"
                                                       {...bvnForm.getInputProps(
                                                            "phone"
                                                       )}
                                                       onChange={(e) => {
                                                            const value =
                                                                 e.target.value;
                                                            const digits = value
                                                                 .replace(
                                                                      /\D/g,
                                                                      ""
                                                                 )
                                                                 .slice(0, 11);
                                                            bvnForm.setFieldValue(
                                                                 "phone",
                                                                 digits
                                                            );
                                                       }}
                                                       styles={{
                                                            input: {
                                                                 fontSize:
                                                                      "16px",
                                                                 fontWeight: 500,
                                                            },
                                                       }}
                                                  />

                                                  <Button
                                                       type="submit"
                                                       loading={isInitializing}
                                                       leftSection={
                                                            <IconRefresh
                                                                 size={18}
                                                            />
                                                       }
                                                       size="lg"
                                                       style={{
                                                            background:
                                                                 "#679369",
                                                            marginTop: "1rem",
                                                       }}
                                                       disabled={
                                                            isInitializing ||
                                                            cooldownTimer > 0
                                                       }
                                                  >
                                                       {isInitializing
                                                            ? "Sending OTP..."
                                                            : cooldownTimer > 0
                                                            ? `Request New Code (${formatTimer(cooldownTimer)})`
                                                            : "Verify BVN"}
                                                  </Button>
                                                  {cooldownTimer > 0 && (
                                                       <Text
                                                            size="xs"
                                                            c="dimmed"
                                                            ta="center"
                                                            mt="xs"
                                                       >
                                                            Please wait{" "}
                                                            {formatTimer(cooldownTimer)}{" "}
                                                            before
                                                            requesting a new OTP
                                                       </Text>
                                                  )}
                                             </Stack>
                                        </form>
                                   </Stack>
                              </Paper>
                         </motion.div>
                    )}

                    {/* OTP Input Section */}
                    {otpSent && (
                         <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4 }}
                         >
                              <Paper
                                   shadow="md"
                                   p="xl"
                                   radius="lg"
                                   style={{
                                        background: "white",
                                        border: "2px solid #e5e7eb",
                                   }}
                              >
                                   <Stack gap="lg">
                                        <div>
                                             <Title
                                                  order={3}
                                                  mb="xs"
                                                  style={{ color: "#2C2C2C" }}
                                             >
                                                  Enter OTP
                                             </Title>
                                             <Text size="sm" c="dimmed">
                                                  Enter the OTP sent to your
                                                  phone number to complete BVN
                                                  verification.
                                             </Text>
                                        </div>

                                        <form
                                             onSubmit={otpForm.onSubmit(
                                                  handleCompleteBvn
                                             )}
                                        >
                                             <Stack gap="md" align="center">
                                                  <div
                                                       style={{
                                                            width: "100%",
                                                            maxWidth: "400px",
                                                       }}
                                                  >
                                                       <Text
                                                            size="sm"
                                                            fw={500}
                                                            mb={5}
                                                            ta="center"
                                                       >
                                                            OTP Code *
                                                       </Text>
                                                       <Text
                                                            size="xs"
                                                            c="dimmed"
                                                            mb="md"
                                                            ta="center"
                                                       >
                                                            Enter the 6-digit
                                                            OTP sent to your
                                                            phone
                                                       </Text>
                                                       <Center>
                                                            <PinInput
                                                                 length={6}
                                                                 size="md"
                                                                 {...otpForm.getInputProps(
                                                                      "otp"
                                                                 )}
                                                                 type="number"
                                                                 oneTimeCode
                                                            />
                                                       </Center>
                                                  </div>

                                                  <Alert
                                                       icon={
                                                            <IconLock
                                                                 size={16}
                                                            />
                                                       }
                                                       title="Security Notice"
                                                       color="yellow"
                                                       radius="md"
                                                  >
                                                       <Text size="sm">
                                                            The OTP is valid for
                                                            a limited time. If
                                                            you didn't receive
                                                            the OTP, please
                                                            check your phone
                                                            number and try
                                                            again.
                                                       </Text>
                                                  </Alert>

                                                  <Group>
                                                       <Button
                                                            variant="subtle"
                                                            onClick={() => {
                                                                 setOtpSent(
                                                                      false
                                                                 );
                                                                 otpForm.reset();
                                                                 setCooldownTimer(
                                                                      0
                                                                 );
                                                            }}
                                                            style={{
                                                                 color: "#679369",
                                                            }}
                                                            disabled={
                                                                 cooldownTimer >
                                                                 0
                                                            }
                                                       >
                                                            {cooldownTimer > 0
                                                                 ? `Back (${formatTimer(cooldownTimer)})`
                                                                 : "Back"}
                                                       </Button>
                                                       <Button
                                                            type="submit"
                                                            loading={
                                                                 isCompleting
                                                            }
                                                            leftSection={
                                                                 <IconCheck
                                                                      size={18}
                                                                 />
                                                            }
                                                            size="lg"
                                                            style={{
                                                                 background:
                                                                      "#679369",
                                                            }}
                                                            disabled={
                                                                 isCompleting
                                                            }
                                                       >
                                                            {isCompleting
                                                                 ? "Verifying..."
                                                                 : "Complete Verification"}
                                                       </Button>
                                                  </Group>
                                             </Stack>
                                        </form>
                                   </Stack>
                              </Paper>
                         </motion.div>
                    )}
               </Stack>
          </Container>
     );
}
